import React from "react";
import PropTypes from "prop-types";
import { css } from "emotion";

// Hangar
import { Icon } from "@maxmilhas/hangar-components-atomic/public";

// Helpers
import { capitalizeFirst } from "../../helpers/string";
import {
	translateDuration,
	timeToString,
	differenceInDays,
	dateToFullString,
	dateToMediumString
} from "../../helpers/dateTime";

// Styles
import {
	segmentBounds,
	timeline,
	time,
	change,
	segmentList,
	classType,
	styleDetails,
	daysAfterDeparture
} from "./style";

const BoundDetails = ({ bound, dictionaries }) => {
	const { location, airline, baggageAllowanceList } = dictionaries;
	const writeCityCountry = locationCode => {
		if (location[locationCode]) {
			return `${location[locationCode].city.name}, ${location[locationCode].country.code}`;
		}
		return locationCode;
	};
	const writeLocationName = locationCode => {
		if (location[locationCode]) {
			return `${location[locationCode].name} (${locationCode})`;
		}
		return locationCode;
	};
	const luggages = {
		checked:
			bound.hasCheckedBags &&
			bound.fareProfile.baggageAllowance.reduce((prev, bag) => {
				if (/^CHECKED/.test(bag)) {
					prev = baggageAllowanceList[bag];
				}
				return prev;
			}, {}),
		carryOn:
			bound.hasCarryOnBags &&
			bound.fareProfile.baggageAllowance.reduce((prev, bag) => {
				if (/^CARRYON/.test(bag)) {
					prev = baggageAllowanceList[bag];
				}
				return prev;
			}, {})
	};
	const luggageContent = key => {
		const checked = key === "checked" && (
			<div className="icon-message green">
				<Icon name="icon-max-communication-circle-check" size="small" />
				<p>
					Inclui <strong>{luggages[key].maximumQuantity}</strong>{" "}
					{luggages[key].maximumQuantity === 1 ? "bagagem" : "bagagens"} para despachar de até{" "}
					<strong>
						{luggages[key].maximumWeightPerUnit}
						{luggages[key].measureUnit.toLowerCase()}
					</strong>
					{luggages[key].maximumQuantity > 1 ? " cada" : ""}.
				</p>
			</div>
		);
		const carryOn = (
			<>
				<div className="icon-message green">
					<Icon name="icon-max-communication-circle-check" size="small" />
					<p>
						Inclui <strong>{luggages[key].maximumQuantity}</strong>{" "}
						{luggages[key].maximumQuantity === 1 ? "bagagem" : "bagagens"} de mão de até{" "}
						<strong>
							{luggages[key].maximumWeightPerUnit}
							{luggages[key].measureUnit.toLowerCase()}
						</strong>
						{luggages[key].maximumQuantity > 1 ? " cada" : ""}.
					</p>
				</div>
				{!luggages.checked && (
					<div className="icon-message red">
						<Icon name="icon-max-communication-circle-close" size="small" />
						<p>
							Não inclui bagagem para despachar. Você pode adicionar esse serviço depois, direto na
							companhia aérea.
						</p>
					</div>
				)}
			</>
		);
		return key === "checked" ? checked : carryOn;
	};
	return (
		<div
			className={css`
				${styleDetails}
			`}
		>
			<div className="header">
				<Icon
					size="small"
					name={`icon-max-travel-plane-${bound.number === 0 ? "outbound" : "inbound"}`}
				/>
				<strong>{bound.number === 0 ? "Ida" : "Volta"}</strong> -{" "}
				{dateToMediumString(bound.departure.dateTime)}
			</div>
			<div className="boundInfo">
				<p className="stretch">
					<span className="departure">{bound.departure.locationCode}</span>
					<strong>{timeToString(bound.departure.dateTime)}</strong>
					<span className="stops">
						{bound.totalStops === 0
							? "Direto"
							: `${bound.totalStops} ${bound.totalStops > 1 ? "paradas" : "parada"}`}
					</span>
					<strong>{timeToString(bound.arrival.dateTime)}</strong>
					<span className="arrival">
						{bound.arrival.locationCode}
						{bound.daysDifference > 0 && <i>+{bound.daysDifference}</i>}
					</span>
				</p>
				<ul className="info">
					<li className="arrive">{dateToFullString(bound.departure.dateTime, true)}</li>
					<li>Duração total de {translateDuration(bound.duration)}</li>
					<li>Classe {bound.cabin === "EX" ? "executiva" : "econômica"}</li>
				</ul>
			</div>
			{(luggages.checked || luggages.carryOn) && (
				<div className="luggage">
					<p className="heading">Bagagem</p>
					{Object.keys(luggages)
						.filter(key => luggages[key] && Object.keys(luggages[key]).length > 0)
						.map((key, idx) => (
							<div key={idx} className="info">
								{luggageContent(key)}
							</div>
						))}
				</div>
			)}
			<div
				className={css`
					${segmentBounds}
				`}
			>
				{bound.segments.map(segment => (
					<React.Fragment key={segment.id}>
						<div
							className={css`
								${timeline}
							`}
						>
							<Icon size="small" color="#223254" name="icon-max-travel-plane-outbound" />
							<span
								className={css`
									${time}
								`}
							>
								<strong>{timeToString(segment.departure.dateTime)}</strong>
							</span>
							<ul
								className={css`
									${segmentList}
								`}
							>
								<li>
									<strong>{writeCityCountry(segment.departure.locationCode)}</strong>
								</li>
								<li>{writeLocationName(segment.departure.locationCode)}</li>
								<li>
									{airline[segment.operatingAirlineCode]
										? capitalizeFirst(
												airline[segment.operatingAirlineCode] || segment.operatingAirlineCode
										  )
										: segment.operatingAirlineCode}{" "}
									- {segment.operatingAirlineCode} {segment.operatingFlightNumber}
								</li>
								<li>{dateToFullString(segment.departure.dateTime)}</li>
								{differenceInDays(segment.departure.dateTime, segment.arrival.dateTime) > 0 && (
									<li
										className={css`
											${daysAfterDeparture}
										`}
									>
										(+{differenceInDays(segment.departure.dateTime, segment.arrival.dateTime)})
										chega{" "}
										{differenceInDays(segment.departure.dateTime, segment.arrival.dateTime) === 1
											? "no dia seguinte"
											: `${differenceInDays(
													segment.departure.dateTime,
													segment.arrival.dateTime
											  )} dias depois`}
									</li>
								)}
								{segment.cabin === "EX" && (
									<li
										className={css`
											${classType}
										`}
									>
										Executiva
									</li>
								)}
							</ul>
						</div>
						{segment.groundOperationalInfo && segment.groundOperationalInfo.type === "connection" && (
							<div
								className={css`
									${timeline}
								`}
							>
								<Icon size="small" name="icon-max-travel-change" />
								<span
									className={css`
										${time}
									`}
								>
									<strong>{timeToString(segment.arrival.dateTime)}</strong>
								</span>
								<ul
									className={css`
										${segmentList}
									`}
								>
									<li>
										<strong>{writeCityCountry(segment.arrival.locationCode)}</strong>
									</li>
									<li>{writeLocationName(segment.arrival.locationCode)}</li>
									<li>{dateToFullString(segment.arrival.dateTime)}</li>
									<li
										className={css`
											${change}
										`}
									>
										{segment.groundOperationalInfo.nextBoardingPoint !==
											segment.arrival.locationCode && "Troca de aeroporto, "}
										{segment.groundOperationalInfo.nextBoardingPoint ===
											segment.arrival.locationCode && "Conexão, "}
										{translateDuration(segment.groundOperationalInfo.duration)}
									</li>
								</ul>
							</div>
						)}
						{segment.stops &&
							segment.stops.map((stop, idx) => (
								<div
									key={idx}
									className={css`
										${timeline}
									`}
								>
									<Icon size="small" name="icon-max-travel-plane-outbound" />
									<span
										className={css`
											${time}
										`}
									>
										<strong>{timeToString(stop.arrivalDateTime)}</strong>
									</span>
									<ul
										className={css`
											${segmentList}
										`}
									>
										<li>
											<strong>{writeCityCountry(stop.locationCode)}</strong>
										</li>
										<li>{writeLocationName(stop.locationCode)}</li>
										<li>{dateToFullString(stop.departureDateTime)}</li>
										<li
											className={css`
												${change}
											`}
										>
											Parada técnica
										</li>
									</ul>
								</div>
							))}
					</React.Fragment>
				))}
				<div
					className={css`
						${timeline}
					`}
				>
					<Icon size="small" name="icon-max-communication-location" />
					<span
						className={css`
							${time}
						`}
					>
						<strong>{timeToString(bound.arrival.dateTime)}</strong>
					</span>
					<ul
						className={css`
							${segmentList}
						`}
					>
						<li>
							<strong>{writeCityCountry(bound.arrival.locationCode)}</strong>
						</li>
						<li>{writeLocationName(bound.arrival.locationCode)}</li>
						<li>{dateToFullString(bound.arrival.dateTime)}</li>
					</ul>
				</div>
			</div>
		</div>
	);
};

BoundDetails.propTypes = {
	bound: PropTypes.object,
	dictionaries: PropTypes.object
};

BoundDetails.defaultProps = {
	bound: null,
	dictionaries: null
};

export default BoundDetails;
